import React, { useRef, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { ReactComponent as PlayIcon } from 'new/components/icons/fi-rr-play.svg';

type Props = {
  value: string;
  isCustom: boolean;
  selectedTableLabel?: string;
  canRun: boolean;
  running: boolean;
  onChange: (value: string) => void;
  onRun: () => void;
  onResetToTable?: () => void;
  onMount?: (editor: unknown, monaco: unknown) => void;
};

const editorOptions = {
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  // Breathing room so the first line isn't flush against the panel's top edge.
  padding: { top: 12, bottom: 10 },
  // Suggest only SQL keywords + schema names (no noisy document-word matches).
  wordBasedSuggestions: false,
};

export const SqlEditorPanel: React.FC<Props> = ({
  value,
  isCustom,
  selectedTableLabel,
  canRun,
  running,
  onChange,
  onRun,
  onResetToTable,
  onMount,
}) => {
  const lineCount = Math.max(value.split('\n').length, 1);

  // handleMount's onMount callback fires once, at editor creation, so a
  // plain closure over canRun/onRun would freeze whatever they were at that
  // moment. Read the latest values from a ref instead (same pattern as
  // TransformDSLEditor's handlersRef).
  const propsRef = useRef({ canRun, onRun });

  useEffect(() => {
    propsRef.current = { canRun, onRun };
  });

  const handleMount = (editor: any, monaco: any) => {
    // Return value is collected by the Monaco wrapper and disposed on unmount,
    // so the completion provider registered in onMount is cleaned up.
    const disposables = onMount?.(editor, monaco);

    // editor.addCommand() registers through monaco's shared
    // StandaloneKeybindingService, which is never disposed and applies
    // across every editor sharing this monaco module -- so once this panel
    // mounts anywhere in the session, its Cmd/Ctrl+Enter handler outranks
    // index.js's rule removal for every other Monaco editor on the page for
    // the rest of the page's life. editor.onKeyDown is per-instance and
    // cleaned up with the editor, so it can't leak.
    editor?.onKeyDown?.((e: any) => {
      if (e.equals((monaco?.KeyMod?.CtrlCmd ?? 0) | (monaco?.KeyCode?.Enter ?? 0))) {
        e.preventDefault();
        e.stopPropagation();

        const { canRun, onRun } = propsRef.current;

        if (canRun) onRun();
      }
    });

    return disposables;
  };

  const showReset = isCustom && !!selectedTableLabel && !!onResetToTable;

  return (
    <div className="db-sql">
      <div className="db-sql-editor">
        <LoadableMonacoEditor
          language="sql"
          theme="dark"
          value={value}
          height="100%"
          onMount={handleMount}
          onChange={onChange}
          options={editorOptions}
        />
      </div>

      <div className="db-sql-footer">
        <div className="db-sql-footer-left">
          <span>
            {lineCount} {lineCount === 1 ? 'line' : 'lines'}
          </span>
          {showReset ? (
            <button type="button" className="db-sql-reset" onClick={onResetToTable}>
              Reset to {selectedTableLabel}
            </button>
          ) : null}
        </div>
        <div className="db-sql-actions">
          <span className="db-sql-kbd">⌘ ↵</span>
          <button type="button" className="db-sql-run" onClick={onRun} disabled={!canRun}>
            {running ? (
              <CircularProgress size={12} sx={{ color: 'var(--color-greyscale-0)' }} />
            ) : (
              <PlayIcon />
            )}
            Run
          </button>
        </div>
      </div>
    </div>
  );
};
